import { AxiosResponse } from 'axios';

import { HttpClient } from 'api/HttpClient';
import { ErrorDto } from 'errors/ErrorDto';
import {
  ResolveResumeProcessLinkTokenResponse,
  SendUpdatePhoneNumberSMSCodeRequest,
  SuccessResponse,
  VerifyEmailVerificationCodeResponse,
  SendUpdateEmailVerificationCodeRequest,
  UpdatePasswordRequest,
  VerifyEmailVerificationCodeRequest,
} from 'api/auth/authApi.types';

export default class AuthApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL + '/account' || '');
  }

  public async createAccount(email: string, password: string, phone: string, borrowerId: string) {
    return this.instance
      .post('', { email: email, password: password, phone: phone, borrowerId: borrowerId })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async login(email?: string, password?: string, refreshToken?: string) {
    if (email && password) {
      return this.instance
        .post('/login', { email: email, password: password })
        .then((response: AxiosResponse<any>) => response.data);
    } else if (refreshToken) {
      return this.instance
        .post('/login', { refreshToken: refreshToken })
        .then((response: AxiosResponse<any>) => response.data);
    }
  }

  public async getCurrentAccount() {
    return this.instance.get(`/current-account`).then((response: AxiosResponse<any>) => response.data);
  }

  public async requestResetPassword(email: string): Promise<{ email: string }> {
    return this.instance
      .post('/request-reset-password', { email: email })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async resetPassword(password: string, resetPasswordToken: string) {
    return this.instance
      .put<SuccessResponse>('/reset-password', {
        password: password,
        resetPasswordToken: resetPasswordToken,
      })
      .then((res) => res.data);
  }

  public async sendEmailCode() {
    return this.instance.post<SuccessResponse>('/email-codes');
  }

  public async verifyEmailCode(code: string): Promise<{ success: boolean }> {
    return this.instance
      .post<SuccessResponse>('/verify-email', {
        code: code,
      })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async logout() {
    return this.instance.post('/logout').then((response: AxiosResponse<any>) => response.data);
  }

  public async verifyPassword(password: string) {
    return this.instance
      .post(`/verify-password`, { password: password })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async sendUpdateEmailVerificationCode({
    email,
    accountPasswordValidationToken,
  }: SendUpdateEmailVerificationCodeRequest) {
    return this.instance
      .put('/send-update-email-verification-code', {
        email: email,
        accountPasswordValidationToken: accountPasswordValidationToken,
      })
      .then((response: AxiosResponse<any | ErrorDto>) => {
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  }

  public async updateEmailAddressByVerifyingCode(code: string) {
    return this.instance
      .put(`/update-email-address-by-verifying-code`, { code: code })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async sendUpdatePhoneNumberSMSCode({
    phone,
    accountPasswordValidationToken,
  }: SendUpdatePhoneNumberSMSCodeRequest) {
    return this.instance
      .put(`/send-update-phone-number-sms-code`, {
        phone: phone,
        accountPasswordValidationToken: accountPasswordValidationToken,
      })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async updatePhoneNumberByVerifyingCode(code: string) {
    return this.instance
      .put(`/update-phone-number-by-verifying-code`, { code: code })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async updatePassword({ oldPassword, newPassword }: UpdatePasswordRequest) {
    return this.instance
      .put(`/password`, { oldPassword: oldPassword, newPassword: newPassword })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async sendOtpCode(borrowerId: string): Promise<void> {
    return this.instance
      .post('/otp-auth/send', { borrowerId: borrowerId, type: 'email' })
      .then((response: AxiosResponse<void>) => response.data);
  }

  public async resendOtpCode(borrowerId: string): Promise<void> {
    return this.instance
      .post('/otp-auth/resend', { borrowerId: borrowerId, type: 'email' })
      .then((response: AxiosResponse<void>) => response.data);
  }

  public async verifyOtpCode({
    borrowerId,
    code,
  }: VerifyEmailVerificationCodeRequest): Promise<VerifyEmailVerificationCodeResponse> {
    return this.instance
      .post('/otp-auth/verify', { borrowerId: borrowerId, code: code })
      .then((response: AxiosResponse<VerifyEmailVerificationCodeResponse>) => response.data);
  }

  public async resolveResumeProcessLinkToken(token: string): Promise<ResolveResumeProcessLinkTokenResponse> {
    return this.instance
      .post('/resume-process-link/resolve', { token: token })
      .then((response: AxiosResponse<ResolveResumeProcessLinkTokenResponse>) => response.data);
  }
}

export const authApi = new AuthApi();
